// Imports
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { authLinkClass, authPrimaryButtonClass } from '@/components/Auth/AuthShell';
import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import type { RequestError } from '@/renderer/utils/errors';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

// Types
type ForgotPasswordData = { email: string };

// API requests
const passwordLinkRequest: RequestSchema<API.ResetState, { email: string }> = {
  name: 'passwordLinkRequest',
  input: '/api/v1/auth/reset2',
  method: 'POST',
};

export default function RequestReset() {
  // Form hooks
  const form = useForm<WithFormErrors<ForgotPasswordData>>();

  // Request hooks
  const request = useRequest(passwordLinkRequest, { manual: true });

  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        try {
          const result = await request.run(body, { throwOnRun: true });

          if (result?.message === 'API.AUTHENTICATION.RESET.SUCCESS') {
            setIsSubmitted(true);
          }
        } catch (error) {
          if ((error as RequestError).data?.code === 404) {
            form.setError('email', {
              type: 'manual',
              message: 'The email is not a valid user account',
            });
          }
        }
      },
    },
    [],
  );

  return (
    <>
      <div className="flex flex-col items-center text-center">
        <h1 className="font-extrabold text-2xl text-zinc-900 tracking-tight dark:text-white">
          Reset your password
        </h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          {isSubmitted
            ? 'Password reset request has been emailed to you.'
            : "Enter your email and we'll send you a link to reset your password."}
        </p>
      </div>

      {isSubmitted ? (
        <div className="mt-7 text-center">
          <a href="/signin" className={authLinkClass}>
            ← Back to sign in
          </a>
        </div>
      ) : (
        <form onSubmit={handleFormSubmit} className="mt-7 space-y-5">
          <FormControl
            form={form}
            name="email"
            label="Primary email address"
            register={{
              required: {
                value: true,
                message: 'Email is required',
              },
              pattern: {
                value: EMAIL_REGEX,
                message: 'Email is not a valid email!',
              },
            }}
            autoCapitalize="off"
            autoCorrect="off"
            autoSave="off"
          />

          <FormErrors
            form={form}
            request={request}
            rewrite={(err) => {
              return err.code === 404 ? '' : err.toString();
            }}
          />

          <FormSubmitButton
            disabled={request.loading}
            loading={!!request.loading}
            className={authPrimaryButtonClass}
          >
            Reset your password
          </FormSubmitButton>

          <p className="text-center text-sm text-zinc-600 dark:text-zinc-400">
            Wrong place?{' '}
            <a href="/signin" className={`whitespace-nowrap ${authLinkClass}`}>
              Back to sign in →
            </a>
          </p>
        </form>
      )}
    </>
  );
}
